import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as SkitterbookIconDark } from '../../public/images/svg/New Skitterbook Logo.svg';
import { ReactComponent as ScrollArrow } from '../../public/images/svg/CircledArrow Down - 02.svg';
import { ReactComponent as ImageCancel } from '../../public/images/svg/ImageCancel.svg';
import { AccountBadge } from '../Account/AccountBadge';
import { FirstUseDisclaimer } from '../assets/Documents/FirstUseDisclaimer';
import { subscribeToAuthChanges } from '../Services/firebase';
import { AnimatedTitleComponent } from '../Title/AnimatedTitleComponent';
import { SkitterBookMaker } from '../Skitterbook/SkitterBookMaker';
import { GlyphButton } from '../Controls/Button/GlyphButton';
import { getSetting, setSetting } from '../Utils/settingsUtils';
import { visualThemeName } from '../Settings/UserSettings';
import { SkitterBookLoader, SkitterBookOnLoadProps } from "../Skitterbook/SkitterBookLoader";
import { DynamicImageWindow } from '../Controls/DynamicImageWindow';
import { DockPanel, DockPanelHandle } from '../Controls/DockPanel/DockPanel';
import { SPAFooter } from './SPAFooter';
import { SPAStickyFooter } from './SPAStickyFooter';
import "./css/SPAFramework.css";
import { ScrollDetacher } from "../Controls/ScrollDetacher";

export const NOT_LOGGED_IN = "not-logged-in";
export const SAMPLE_FIELD = "Sample";

const STATE_FILE = 1;
const STATE_SKITTERBOOK = 2;

const SPAFramework: React.FC = () => {
    const [scrolledDown, setScrolledDown] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState<boolean | null>(null)
    const [canScrollUp, setCanScrollUp] = useState<boolean | null>(null)
    const [showScrollIndicators, setShowScrollIndicators] = useState(false);
    const [showHeader, setShowHeader] = React.useState(true);
    const [accountId, setAccountId] = useState<string>();
    const [policiesAccepted, setPoliciesAccepted] = useState<boolean | null>(null);
    const [visualTheme, setVisualTheme] = useState<string | null>(null);
    const [bodyHeightPixels, setBodyHeightPixels] = useState<number | null>(null);
    const [skitterbookId, setSkitterbookId] = useState<string | null>(null);
    const [skitterbookVideoFile, setSkitterbookVideoFile] = useState<ArrayBuffer | null>(null);
    const [skitterbookVideoFileName, setSkitterbookVideoFileName] = useState<string | null>(null);
    const [skitterbookState, setSkitterbookState] = useState<string | null>(null);
    const scrollSectionRef = useRef<HTMLElement | null>(null);

    const topLeftDockPanelRef = useRef<DockPanelHandle | null>(null);
    const topRightDockPanelRef = useRef<DockPanelHandle | null>(null);

    useEffect(() => {
        if (!history.state) {
            history.replaceState({ loaded: false }, "");
        }

        const handlePop = () => {
            // User pressed Back → reset file
            setSkitterbookState(null);
            setSkitterbookId(null);
            setSkitterbookVideoFile(null);
            setSkitterbookVideoFileName(null);
        };

        window.addEventListener("popstate", handlePop);
        return () => window.removeEventListener("popstate", handlePop);
    }, []);

    subscribeToAuthChanges((user) => {
        if (user) {
            setAccountId(user.uid); // Invoke the default for the most recent account skitterbook
        } else {
            setAccountId(NOT_LOGGED_IN); // Invoke the default for either a sample or a session skitterbook.
        }
    });

    useEffect(() => {
        const ro = new ResizeObserver(() => {
            setBodyHeightPixels(window.innerHeight);
        })

        if (scrollSectionRef.current) {
            ro.observe(scrollSectionRef.current)
        }

        return () => {
            if (ro) {
                ro.disconnect();
            }
        }
    }, [visualTheme])

    useEffect(() => {
        const el = scrollSectionRef.current as HTMLElement | null;

        const handleTouch = () => {
            setShowScrollIndicators(true);

            const ssInterval = setInterval(() => {
                clearInterval(ssInterval);
                setShowScrollIndicators(false);
            }, 3000);
        }

        const handleScroll = () => {
            if (!el) return;

            const scrolledDown = el.scrollTop > 50;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight;

            setScrolledDown(scrolledDown);
            setCanScrollDown(!atBottom);
            setCanScrollUp(el.scrollTop > 0);

            handleTouch();
        };

        if (el) {
            el.addEventListener("scroll", handleScroll);
            el.addEventListener("touchstart", handleTouch);
            el.addEventListener("pointerdown", handleTouch);

            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight;
            setCanScrollDown(!atBottom);
            setCanScrollUp(el.scrollTop > 0);
        }

        return () => {
            if (el) {
                el.removeEventListener("scroll", handleScroll);
                el.removeEventListener("touchstart", handleTouch);
                el.removeEventListener("pointerdown", handleTouch);
            }
        };
    }, [visualTheme]);


    useEffect(() => {
        if (accountId) {
            const vth = getSetting<string>(visualThemeName, "dark");
            setVisualTheme(vth);

        }
    }, [policiesAccepted, accountId]);


    const onPolicyDisclaimerAccept = () => {
        setSetting<boolean>("policy-disclaimer-accepted", true);
        setPoliciesAccepted(true);
    }

    const skitterookLoaderOnLoadHandler = (props: SkitterBookOnLoadProps) => {
        setSkitterbookVideoFile(props.videoBuffer);
        setSkitterbookVideoFileName(props.videoName);

        history.pushState({ loaded: STATE_FILE }, "");
    }

    const skitterBookFooterContent = <footer className={`spa-footer}`}><SPAFooter /></footer>

    const cancelLoadedVideoContent = <GlyphButton ariaLabel="cancel-loaded-video"
        onClick={() => {
            history.back();
        }}
        IconComponent={<ImageCancel width={32} height={32} viewBox="0 0 32 32" />} />

    const spaFrameworkContent = visualTheme
        ? <div className={`spa-framework ${scrolledDown ? "scrolled-down" : ""} ${canScrollDown ? "can-scroll-down" : ""} 
        ${canScrollUp ? "can-scroll-up" : ""} settle-effect ${visualTheme} ${showScrollIndicators ? "show-scroll-indicators" : ""} `}>
            {policiesAccepted === false ? <FirstUseDisclaimer onAccept={onPolicyDisclaimerAccept} /> : <></>}
            {
                (skitterbookVideoFile === null ||
                    skitterbookVideoFileName === null) &&
                (
                    <div className="skitterbook-background-image-container">
                        <DynamicImageWindow
                            portraitImage="/images/webp/SkitterbookPortrait.webp"
                            landscapeImage="/images/webp/SkitterbookLandscape.webp"
                        />
                    </div>
                )
            }
            <header className={`spa-header ${showHeader ? "visible" : "hidden"}`}>
                <SkitterbookIconDark width={47.637} height={28.715} viewBox="0 0 47.637 28.715" />
                <AnimatedTitleComponent />
                <AccountBadge />
            </header>
            <section className={`spa-scroll-section pretty-scroll ${visualTheme}`} ref={scrollSectionRef}>
                <div className="section-body">
                    <DockPanel id="framework-top-left" dock="top-left" ref={topLeftDockPanelRef} />
                    <DockPanel id="framework-top-right" dock="top-right" ref={topRightDockPanelRef} />
                    <SkitterBookLoader skitterbookId={null}
                        videoBuffer={skitterbookVideoFile}
                        onLoad={skitterookLoaderOnLoadHandler}
                        dockPanel={topLeftDockPanelRef} />
                    <div className="spa-framework-content">
                        {
                            skitterbookVideoFile && skitterbookVideoFileName &&
                            <div className="skitterbook-maker-outer-container">
                                <ScrollDetacher id="cancel-loaded-video-scroll-detacher"
                                    alternativeContent={cancelLoadedVideoContent}
                                    dockPanel={topRightDockPanelRef}>
                                    {cancelLoadedVideoContent}
                                </ScrollDetacher>
                                <SkitterBookMaker videoBuffer={skitterbookVideoFile} videoName={skitterbookVideoFileName} dockPanel={topLeftDockPanelRef} />
                            </div>
                        }
                    </div>
                </div>
                {skitterBookFooterContent}
            </section>
            <ScrollArrow className="scroll-down-indicator" width={48} height={48} viewBox="0 0 32 32" />
            <ScrollArrow className="scroll-up-indicator" width={48} height={48} viewBox="0 0 32 32" />
            <footer>
                {accountId && bodyHeightPixels &&
                    <SPAStickyFooter onVisibilityChange={(value) => setShowHeader(true)} />
                }

            </footer>
        </div>
        : <></>

    return (
        <>{spaFrameworkContent}</>
    );
};

export default SPAFramework;
